/**
 * Route Configuration with Access Control
 * 
 * Centralized configuration for all routes with their access policies.
 * This is the single source of truth for route-based authorization.
 */

export enum UserType {
    PARTNER = 'partner',
    REGULAR = 'regular',
}

export enum RouteAccessLevel {
    PUBLIC = 'public',                // Anyone can access (landing, sign-in, etc.)
    AUTHENTICATED = 'authenticated',   // Any authenticated user
    PARTNER_ONLY = 'partner_only',    // Only partners
    REGULAR_ONLY = 'regular_only',    // Only regular users
}

export interface RouteConfig {
    path: string;
    accessLevel: RouteAccessLevel;

    // Account status requirements
    requireEmailVerified?: boolean;
    requireProfileCompleted?: boolean;
    requirePartnerVerified?: boolean;

    // Redirects for unauthorized access
    redirectTo?: {
        partner?: string;   // Where to redirect partners if they can't access
        regular?: string;   // Where to redirect regular users if they can't access
    };
}

/**
 * Route Configuration Map
 * 
 * Define access policies for all application routes here.
 */
export const ROUTE_CONFIGS: Record<string, RouteConfig> = {
    // ============================================================================
    // PUBLIC ROUTES - No authentication required
    // ============================================================================
    '/': {
        path: '/',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/about': {
        path: '/about',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/contact': {
        path: '/contact',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/support': {
        path: '/support',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/m/:shortCode': {
        path: '/m/:shortCode',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/sign-in': {
        path: '/sign-in',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/sign-up': {
        path: '/sign-up',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/forgot-password': {
        path: '/forgot-password',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/reset-password': {
        path: '/reset-password',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/verify-otp': {
        path: '/verify-otp',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/verify-email': {
        path: '/verify-email',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    // ============================================================================
    // PARTNER-ONLY ROUTES
    // ============================================================================
    '/partner/dashboard': {
        path: '/partner/dashboard',
        accessLevel: RouteAccessLevel.PARTNER_ONLY,
        requireEmailVerified: true,
        requirePartnerVerified: true,
        redirectTo: {
            regular: '/home',
        },
    },

    '/partner/profile': {
        path: '/partner/profile',
        accessLevel: RouteAccessLevel.PARTNER_ONLY,
        requireEmailVerified: true,
        redirectTo: {
            regular: '/home',
        },
    },

    '/partner/pages/create': {
        path: '/partner/pages/create',
        accessLevel: RouteAccessLevel.PARTNER_ONLY,
        requireEmailVerified: true,
        requirePartnerVerified: true,
        redirectTo: {
            regular: '/memorial/create',
        },
    },

    '/partner/complete-profile': {
        path: '/partner/complete-profile',
        accessLevel: RouteAccessLevel.PARTNER_ONLY,
        requireEmailVerified: true,
        requirePartnerVerified: false, // Can access before full verification
        redirectTo: {
            regular: '/home',
        },
    },

    '/partner/verify-email': {
        path: '/partner/verify-email',
        accessLevel: RouteAccessLevel.PUBLIC, // Email verification happens before sign-in
        requireEmailVerified: false,
    },

    '/partner/verification-pending': {
        path: '/partner/verification-pending',
        accessLevel: RouteAccessLevel.PARTNER_ONLY,
        requireEmailVerified: true,
        requirePartnerVerified: false, // Waiting for verification
        redirectTo: {
            regular: '/home',
        },
    },

    '/partner/verification-rejected': {
        path: '/partner/verification-rejected',
        accessLevel: RouteAccessLevel.PARTNER_ONLY,
        requireEmailVerified: true,
        redirectTo: {
            regular: '/home',
        },
    },

    // ============================================================================
    // REGULAR USER-ONLY ROUTES
    // ============================================================================
    '/home': {
        path: '/home',
        accessLevel: RouteAccessLevel.REGULAR_ONLY,
        requireEmailVerified: true,
        redirectTo: {
            partner: '/partner/dashboard',
        },
    },

    '/my-pages': {
        path: '/my-pages',
        accessLevel: RouteAccessLevel.REGULAR_ONLY,
        requireEmailVerified: true,
        redirectTo: {
            partner: '/partner/dashboard',
        },
    },

    '/memorial/create': {
        path: '/memorial/create',
        accessLevel: RouteAccessLevel.REGULAR_ONLY,
        requireEmailVerified: true,
        redirectTo: {
            partner: '/partner/pages/create',
        },
    },

    // ============================================================================
    // SHARED AUTHENTICATED ROUTES - Both user types can access
    // ============================================================================
    '/account': {
        path: '/account',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    '/settings': {
        path: '/settings',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    '/notifications': {
        path: '/notifications',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    '/contacts': {
        path: '/contacts',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    '/join/:inviteCode': {
        path: '/join/:inviteCode',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/chats': {
        path: '/chats',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    '/search': {
        path: '/search',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    // Memorial pages - anyone can view (public or authenticated)
    '/memorial/:slug': {
        path: '/memorial/:slug',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/memorial/:slug/edit': {
        path: '/memorial/:slug/edit',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    '/memorial/:slug/admin': {
        path: '/memorial/:slug/admin',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    '/memorial/:slug/settings': {
        path: '/memorial/:slug/settings',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    '/memorial/:slug/venue-screens': {
        path: '/memorial/:slug/venue-screens',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },

    '/profile/:slug': {
        path: '/profile/:slug',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/atag/:slug': {
        path: '/atag/:slug',
        accessLevel: RouteAccessLevel.PUBLIC,
    },

    '/ceremony/:ceremonyId': {
        path: '/ceremony/:ceremonyId',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: false,
    },

    // ============================================================================
    // STATUS PAGES
    // ============================================================================
    '/account-suspended': {
        path: '/account-suspended',
        accessLevel: RouteAccessLevel.PUBLIC, // Anyone can see this
    },

    '/profile-completion': {
        path: '/profile-completion',
        accessLevel: RouteAccessLevel.AUTHENTICATED,
        requireEmailVerified: true,
    },
};

/**
 * Get route configuration for a given path
 * Supports dynamic routes with parameters
 */
export function getRouteConfig(path: string): RouteConfig | null {
    // Try exact match first
    if (ROUTE_CONFIGS[path]) {
        return ROUTE_CONFIGS[path];
    }

    // Try pattern matching for dynamic routes
    for (const [pattern, config] of Object.entries(ROUTE_CONFIGS)) {
        if (matchRoute(pattern, path)) {
            return config;
        }
    }

    return null;
}

/**
 * Match a route pattern against a path
 * Supports :param syntax
 */
function matchRoute(pattern: string, path: string): boolean {
    const patternParts = pattern.split('/');
    const pathParts = path.split('/');

    if (patternParts.length !== pathParts.length) {
        return false;
    }

    return patternParts.every((part, i) => {
        return part.startsWith(':') || part === pathParts[i];
    });
}
